import React, { useState, useCallback, memo, useRef } from 'react';

import {
  CloseIconWrapper,
  MenuButton,
  MenuIcon,
  NavigationHeader,
  NavigationIcon,
  NavigationItem,
  NavigationLabel,
  NavigationTitle,
  NavigationTitleWrapper,
  NavigationItemsList,
  NavigationItemWrapper,
} from './styles';
import Icon from 'components/Icon';
import CountDownTimer from '../CountDownTimer/CountDownTimer';
import LocalIcon from '../../ui/LocalIcon';
import SelectWithPopover from '../Select/SelectWithPopover';
import { getDomainAndPort } from '../../api/Utils';

interface IComponentProps {
  defaultTab: string;
  items: INavigationItem[];
  onClose?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMinimize?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onSelect: (id: any) => any;
  showItems?: boolean;
  title: string;
  versionItems?: { value: string; label: string }[];
  currentVersion?: string;
  setVersion?: (versionId: string) => void;
  showHome?: boolean;
  customDropDown?: React.ReactElement;
  PaginatedReportHandler?: React.ReactElement;
  timerDue?: number;
  children?: React.ReactNode;
  uniqueTags?: string[];
  selectedCategorizedTag?: string;
  onSetSelectedCategorizedTag?: (tag: string) => void;
}

interface INavigationItem {
  id: string;
  label: string;
}

const Navigation: React.FC<IComponentProps> = ({
  children,
  defaultTab,
  items,
  onClose,
  onMinimize,
  onSelect,
  showItems = true,
  title,
  versionItems,
  currentVersion,
  setVersion,
  showHome,
  customDropDown,
  timerDue,
  PaginatedReportHandler,
}) => {
  const selectedItemIndex = items.findIndex((item) => item.id === defaultTab);
  const [selectedId, setSelectedId] = useState(selectedItemIndex);

  const menuButton = useRef<HTMLInputElement>(null);
  const handleClick =
    (index: number, item: INavigationItem) =>
    (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
      e.preventDefault();
      setSelectedId(index);
      onSelect(item.id);
      menuButton.current && menuButton.current.click();
    };
  const activeVersionItem =
    versionItems && versionItems.find((it) => it.value === currentVersion);
  const handleClickCallback = useCallback(handleClick, []);

  return (
    <NavigationHeader>
      <div className="flex flex-nowrap">
        {onClose && (
          <CloseIconWrapper onClick={onClose}>
            <LocalIcon iconName="close-square.svg" color="no-color" type="sm" />
          </CloseIconWrapper>
        )}
        {showHome && (
          <CloseIconWrapper
            onClick={() => {
              if (
                getDomainAndPort().view === 'public' ||
                getDomainAndPort().view === 'publicSingle'
              )
                window.location.href = '/';
              else window.location.href = '/homepage';
            }}
          >
            <Icon type="home" />
          </CloseIconWrapper>
        )}
        {onMinimize && (
          <CloseIconWrapper onClick={onMinimize}>
            <LocalIcon iconName="minimize.svg" color="no-color" type="sm" />
          </CloseIconWrapper>
        )}
      </div>
      {timerDue && <CountDownTimer targetTime={timerDue} />}

      <NavigationTitleWrapper isCenter={!showItems}>
        <NavigationTitle>{title}</NavigationTitle>
        {versionItems && versionItems.length > 1 && (
          <span style={{ marginInlineStart: '1rem', fontSize: '0.8rem' }}>
            <SelectWithPopover
              onChangeValues={(values) => {
                setVersion!(values[0]!.value.toString());
              }}
              isRequired={true}
              options={versionItems}
              allowRemove={false}
              values={[activeVersionItem!]}
              round
            />
          </span>
        )}
        {customDropDown}
      </NavigationTitleWrapper>
      {PaginatedReportHandler || <div style={{ flex: 1 }} />}

      {showItems && (
        <NavigationItemWrapper>
          <MenuButton type="checkbox" id="menu-btn" ref={menuButton} />
          <MenuIcon htmlFor="menu-btn">
            <NavigationIcon />
          </MenuIcon>
          <NavigationItemsList>
            {items.map((item, index) => (
              <NavigationItem
                isActive={selectedId === index}
                key={item.id}
                onClick={handleClickCallback(index, item)}
              >
                <NavigationLabel>{item.label}</NavigationLabel>
              </NavigationItem>
            ))}
            {/* USAGE ???? ???? ???? */}
            {/*<NavigationOverlay index={selectedId} count={count} />*/}
          </NavigationItemsList>
        </NavigationItemWrapper>
      )}
      {children}
    </NavigationHeader>
  );
};

export default memo(Navigation);
